import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Spinner } from "react-bootstrap";
import { getAdherents } from "../api/api";

function initials(user) {
  const first = user?.first_name?.[0] || "";
  const last = user?.last_name?.[0] || "";
  return `${first}${last}`.toUpperCase() || "AD";
}

function displayName(user) {
  return `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.matricule || "Adherent";
}

function minutesSince(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
}

function statusLabel(user) {
  const minutes = minutesSince(user.last_login);
  if (minutes === null) return "Jamais connecte";
  if (minutes < 2) return "Connecte maintenant";
  if (minutes < 60) return `Actif il y a ${minutes} min`;
  if (minutes < 1440) return `Actif il y a ${Math.round(minutes / 60)} h`;
  return `Actif il y a ${Math.round(minutes / 1440)} j`;
}

function isRecentlyConnected(user) {
  const minutes = minutesSince(user.last_login);
  return minutes !== null && minutes <= 480;
}

export default function ConnectedAdherentsDock() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isAdmin = Boolean(user.is_amical_admin || user.is_staff || user.is_superuser);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adherents, setAdherents] = useState([]);

  const loadAdherents = async () => {
    setLoading(true);
    try {
      const res = await getAdherents();
      setAdherents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAdherents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadAdherents();
    const timer = window.setInterval(loadAdherents, 60000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const sorted = useMemo(
    () =>
      [...adherents].sort((a, b) => {
        const aRecent = isRecentlyConnected(a) ? 1 : 0;
        const bRecent = isRecentlyConnected(b) ? 1 : 0;
        if (aRecent !== bRecent) return bRecent - aRecent;
        return new Date(b.last_login || 0) - new Date(a.last_login || 0);
      }),
    [adherents]
  );

  const onlineCount = sorted.filter(isRecentlyConnected).length;

  if (!isAdmin) return null;

  return (
    <div className={`connected-dock ${onlineCount > 0 ? "has-online" : "is-offline"}`}>
      {open && (
        <section className="connected-panel" aria-label="Adherents connectes">
          <div className="connected-panel__header">
            <div>
              <strong>Adherents connectes</strong>
              <span>{onlineCount} actifs recemment</span>
            </div>
            <Button size="sm" variant="outline-secondary" onClick={loadAdherents}>
              {loading ? <Spinner size="sm" /> : "Actualiser"}
            </Button>
          </div>

          <div className="connected-panel__list">
            {sorted.length === 0 && (
              <div className="connected-empty">Aucun adherent a afficher.</div>
            )}

            {sorted.map((item) => {
              const online = isRecentlyConnected(item);
              return (
                <article className={`connected-person ${online ? "is-online" : "is-offline"}`} key={item.id}>
                  <div className="connected-avatar">
                    {initials(item)}
                    <span className={online ? "is-online" : ""} />
                  </div>
                  <div>
                    <strong>{displayName(item)}</strong>
                    <span>{item.matricule} - {item.telephone || "Sans telephone"}</span>
                    <small>{online ? statusLabel(item) : `Hors ligne - ${statusLabel(item)}`}</small>
                  </div>
                  {item.is_amical_admin && (
                    <Badge bg="warning" text="dark">Admin</Badge>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      <button className="connected-toggle" type="button" onClick={() => setOpen((value) => !value)}>
        <span className="connected-toggle__dots">
          <i />
          <i />
          <i />
        </span>
        <strong>{onlineCount}</strong>
      </button>
    </div>
  );
}
